import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Container,
  TextField,
  Button,
  MenuItem,
  FormControl,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ErrorPage from "./error-page";

const ChatHeader = () => {
  useEffect(() => {
    document.title = "Booking";
  }, []);

  return (
    <Typography variant="h4" align="center" gutterBottom>
      Book Now
    </Typography>
  );
};

function App() {
  const [selectedDate, setSelectedDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [userCredentials, setUserCredentials] = useState(null);
  const { id, user_name, key, user_type } = useParams();
  const [isBookingConfirmed, setBookingConfirmed] = useState(false);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    fetchUserCredentials(id, user_name, key, user_type);
    fetchTeachers();
  }, [id, user_name, key, user_type]);

  const fetchUserCredentials = async (id, user_name, key, user_type) => {
    const apiUrl = "/api/get_user_credentials";

    try {
      const responseData = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: id,
          user_name: user_name,
          key: key,
          user_type: user_type,
        }),
      });

      if (!responseData.ok) {
        throw new Error(`Error: ${responseData.status} - ${responseData.statusText}`);
      }

      const result = await responseData.json();
      setUserCredentials(result);

      const checkUserUrl = "/api/check_user";
      const response = await fetch(checkUserUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: result.id,
          user_name: result.user_name,
          key: result.key,
          user_type: result.user_type,
        }),
      });

      if (response.ok) {
        const userExistsResult = await response.json();
        setUserExists(userExistsResult.exists);
      } else {
        console.error(`Error: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error fetching user credentials:", error.message);
    }
  };

  useEffect(() => {
    fetchUserCredentials(id, user_name, key, user_type);
  }, [id, user_name, key, user_type]);

  const fetchTeachers = async () => {
    try {
      const response = await fetch('/api/teachers');
      const data = await response.json();

      setTeachers(
        Array.isArray(data)
          ? data.map((teacher) => ({
              id: teacher.id,
              user_name: teacher.user_name,
            }))
          : []
      );
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  if (!userExists) {
    return <div><ErrorPage /></div>;
  }

  const resetForm = () => {
    setSelectedDate("");
    setStartTime("");
    setEndTime("");
    setSelectedTeacher("");
    setBookingConfirmed(false);
  };
  const handleBookNow = async () => {
    if (!selectedDate || !startTime || !endTime || !selectedTeacher || !isBookingConfirmed) {
      alert("Please fill out all the required fields and confirm the booking before proceeding.");
      return;
    }
  
    const bookingData = {
      selectedDate: selectedDate,
      startTime: startTime,
      endTime: endTime,
      selectedTeacher: parseInt(selectedTeacher), // Convert to integer
      selectedStudent: parseInt(id), // Convert to integer
    };
  
    const apiUrl = "/api/bookadmin_handler";
  
    try {
      console.log("Booking Data:", bookingData); // Log the booking data
  
      const responseData = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });
  
      if (!responseData.ok) {
        throw new Error(`Error: ${responseData.status} - ${responseData.statusText}`);
      }
  
      const result = await responseData.json();
      console.log("Booking successful:", result);
  
      resetForm();
    } catch (error) {
      console.error("Error during request setup:", error);
  
      if (error.response) {
        console.error("Server responded with errors:", error.response.data);
      }
    }
  };
  

  const currentDate = new Date().toISOString().split("T")[0];
  const teacherItems = Array.isArray(teachers)
    ? teachers.map((teacher) => (
        <MenuItem key={teacher.id} value={teacher.id}>
          {teacher.user_name}
        </MenuItem>
      ))
    : null;

  return (
    <div className="App">
      <div className="backgroundImage"></div>
      <Container maxWidth="sm" sx={{ pt: 2 }}>
        <ChatHeader />
        <Grid item xs={12}>
          <FormControl fullWidth sx={{ marginBottom: 2, justifyContent: 'center' }}>
            <TextField
              select
              label="Select Teacher"
              id="teacher-select"
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              SelectProps={{
                renderValue: (value) => {
                  const selectedTeacherObj = teachers.find((teacher) => teacher.id === value);
                  return selectedTeacherObj ? selectedTeacherObj.user_name : '';
                },
              }}
            >
              {teacherItems}
            </TextField>
          </FormControl>
        </Grid>

        {selectedTeacher && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Select Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: currentDate }}
              />
            </Grid>

            {selectedDate && (
              <React.Fragment>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="Start Time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    {Array.from({ length: (23 - 6) * 2 + 1 }, (_, index) => {
                      const hours = Math.floor(index / 2) + 6;
                      const minutes = index % 2 === 0 ? '00' : '30';
                      const time24hr = `${hours.toString().padStart(2, '0')}:${minutes}`;

                      return (
                        <MenuItem key={time24hr} value={time24hr}>
                          {time24hr}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="End Time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    {Array.from({ length: (22 - 6) * 2 + 1 }, (_, index) => {
                      const hours = Math.floor(index / 2) + 7;
                      const minutes = index % 2 === 0 ? '00' : '30';
                      const time24hr = `${hours.toString().padStart(2, '0')}:${minutes}`;

                      return (
                        <MenuItem key={time24hr} value={time24hr}>
                          {time24hr}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
              </React.Fragment>
            )}

            {endTime && (
              <React.Fragment>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isBookingConfirmed}
                        onChange={(e) => setBookingConfirmed(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="I confirm the booking"
                  />
                </Grid>
              </React.Fragment>
            )}
            {isBookingConfirmed && (
              <Grid item xs={12}>
                <Button variant="contained" color="primary" onClick={handleBookNow}>
                  Book Now
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </div>
  );
}

export default App;
